/**
 * O tipo de um aviso do Mural, como ele aparece na etiqueta.
 *
 * Ponto 37 da Gisele (20/10/2026): 'Tag "Comunicado temporário": nova tag para
 * identificar e categorizar esse tipo de aviso.'
 *
 * ## Por que o tipo é DERIVADO, e não uma coluna nova
 *
 * O aviso temporário já se distingue sozinho: é um comunicado com
 * `expires_at`. Guardar "é temporário" numa coluna criaria um segundo lugar
 * onde a mesma coisa está escrita, e os dois podem discordar -- um aviso
 * marcado como temporário sem data, ou um com data que a etiqueta chama de
 * comunicado comum. Quem tem o prazo é a data; a etiqueta só a lê.
 *
 * Isso também é o que faz a etiqueta valer para os avisos que JÁ ESTÃO
 * gravados, sem migração nenhuma e sem alguém ter de reabrir cada um.
 *
 * ## O prazo vencido continua sendo temporário
 *
 * Passada a data, o aviso sai do ícone ao lado do sino e continua no Mural.
 * Ele não vira "Comunicado": ele FOI um comunicado temporário, e é isso que
 * explica por que ele não está mais em destaque. A etiqueta é a mesma; quem
 * diz que o destaque acabou é a linha de prazo (`prazoDoComunicado`).
 */

/** O `kind` que a api grava, por aviso. */
export const KIND_RELEASE = 'release';
export const KIND_COMUNICADO = 'comunicado';
export const KIND_OBSERVACAO = 'observacao-cliente';

/** O tipo de verdade, como a tela categoriza. */
export const TIPO_RELEASE = 'release';
export const TIPO_COMUNICADO = 'comunicado';
export const TIPO_COMUNICADO_TEMPORARIO = 'comunicado-temporario';
export const TIPO_OBSERVACAO = 'observacao-cliente';

export const ROTULO_DO_TIPO = {
    [TIPO_RELEASE]: 'Release',
    [TIPO_COMUNICADO]: 'Comunicado',
    [TIPO_COMUNICADO_TEMPORARIO]: 'Comunicado temporário',
    /*
     * A observação de cliente espelhada no Mural (Gisele, 19/10/2026).
     *
     * Rótulo próprio porque ela não foi escrita como aviso: é o campo do
     * cadastro do cliente aparecendo aqui, e o leitor precisa saber disso --
     * um "Comunicado" sobre um cliente específico pareceria um recado da
     * diretoria.
     */
    [TIPO_OBSERVACAO]: 'Observação de cliente',
};

/**
 * As opções do filtro "Tipo", na ordem em que fazem sentido para quem lê.
 *
 * A observação de cliente entra: ela está na lista do Mural e é justamente a
 * que alguém pode querer separar do resto. O que ela NÃO faz é aparecer no
 * seletor de publicar -- essas linhas nascem do cadastro do cliente, e a api
 * só aceita `release` e `comunicado`.
 */
export const TIPOS_PARA_FILTRAR = [
    TIPO_COMUNICADO,
    TIPO_COMUNICADO_TEMPORARIO,
    TIPO_RELEASE,
    TIPO_OBSERVACAO,
];

/**
 * O tipo de um aviso.
 *
 * @param {object} aviso a linha como a api a devolve (`kind`, `expiresAt`)
 * @returns {string} um dos `TIPO_*`, ou o `kind` cru quando ele é desconhecido
 */
export const tipoDoAviso = (aviso) => {
    const kind = aviso?.kind;

    // `kind` novo que a api passe a mandar não pode virar "Comunicado" por
    // acidente: devolver o valor cru faz a etiqueta mostrar o nome dele, que é
    // feio mas verdadeiro, em vez de mentir.
    if (kind !== KIND_COMUNICADO) return kind || '';

    return aviso?.expiresAt ? TIPO_COMUNICADO_TEMPORARIO : TIPO_COMUNICADO;
};

/** O texto da etiqueta. Tipo desconhecido aparece cru, e não vazio. */
export const rotuloDoTipoDoAviso = (aviso) => {
    const tipo = tipoDoAviso(aviso);

    return ROTULO_DO_TIPO[tipo] || tipo;
};

/**
 * O aviso é do tipo escolhido no filtro?
 *
 * Filtro vazio aceita tudo -- é o que "ignorar campo vazio" significa no
 * diálogo de filtros do Mural.
 */
export const avisoCasaOTipo = (aviso, tipo) => {
    if (!tipo) return true;

    return tipoDoAviso(aviso) === tipo;
};
